import { useEffect, useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { IoMdClose } from 'react-icons/io';
import { getAllRoles } from '../../../../redux/UserReducer/action';
import { AUTHORABLE_ACTION_TYPES } from '../../../../config/stage_transition_rules';
import './RuleBuilderDrawer.css';
import { getAuthToken } from "../../../../utils/auth";

const EMPTY_ACTION = () => ({ action_type: AUTHORABLE_ACTION_TYPES[0], action_config: '{}' });

// Rules created via bulkGenerateTransitionRules have no from/to editable here —
// this builder only supports creating a fresh rule or editing an existing
// one's label/permissions/reason/two-person-rule/active state + its actions,
// matching exactly what the backend's updateTransitionRule accepts (it does
// not accept from_stage_id/to_stage_id/direction/scope changes on update).
const RuleBuilderDrawer = ({ rule, onClose, onSaved }) => {
    const isEditing = !!rule?.stage_transition_id;
    const dispatch = useDispatch();

    const { rolesList } = useSelector((store) => ({
        rolesList: store.UserReducer.roles.rolesList,
    }));

    const [stages, setStages] = useState([]);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        label: rule?.label || '',
        from_stage_id: rule?.from_stage_id || '',
        to_stage_id: rule?.to_stage_id || '',
        direction: rule?.direction || 'BACKWARD',
        scope: rule?.scope || 'APPLICATION',
        allowed_role_ids: rule?.allowed_role_ids?.map(String) || [],
        requires_two_person_rule: rule?.requires_two_person_rule || false,
        requires_reason: rule?.requires_reason || false,
        reason_categories: (rule?.reason_categories || []).join(', '),
        is_active: rule?.is_active ?? true,
    });

    const [actions, setActions] = useState(
        (rule?.actions || [])
            .filter((a) => a.action_type !== 'LOG_TRANSITION')
            .sort((a, b) => a.sequence_order - b.sequence_order)
            .map((a) => ({ action_type: a.action_type, action_config: JSON.stringify(a.action_config || {}, null, 2) }))
    );

    const authToken = useMemo(() => {
        // const tokens = JSON.parse(sessionStorage.getItem('tokens')) || null;
        // return tokens?.token || null;
        return getAuthToken();
    }, []);

    const headers = { Authorization: `Bearer ${authToken}`, 'Content-type': 'application/json' };

    useEffect(() => {
        dispatch(getAllRoles(1, 1000, {}));

        axios
            .get(`${process.env.REACT_APP_API_BASE_URL}/application-stage/get-application-stages`, { headers })
            .then((resp) => setStages(resp?.data?.data || []))
            .catch(() => toast.error('Failed to load stages'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fromStage = stages.find((s) => String(s.stage_id) === String(form.from_stage_id));

    const hardLockWarning =
        form.scope === 'APPLICATION' && form.direction === 'BACKWARD' && fromStage?.hard_lock
            ? `"${fromStage.stage_name}" is hard-locked — the backend will refuse to save a BACKWARD rule from this stage.`
            : null;

    const toggleRole = (roleId) => {
        setForm((prev) => {
            const id = String(roleId);
            const set = new Set(prev.allowed_role_ids);
            set.has(id) ? set.delete(id) : set.add(id);
            return { ...prev, allowed_role_ids: Array.from(set) };
        });
    };

    const addAction = () => setActions((prev) => [...prev, EMPTY_ACTION()]);
    const removeAction = (index) => setActions((prev) => prev.filter((_, i) => i !== index));
    const updateAction = (index, patch) =>
        setActions((prev) => prev.map((a, i) => (i === index ? { ...a, ...patch } : a)));
    const moveAction = (index, direction) => {
        setActions((prev) => {
            const next = [...prev];
            const target = index + direction;
            if (target < 0 || target >= next.length) return prev;
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    // Authoring aid: ask the backend which data the stages between From and To own
    // and add (or refresh) one INVALIDATE_DATA action. Editable before saving.
    const canSuggest = form.scope === 'APPLICATION' && form.direction === 'BACKWARD' && form.from_stage_id && form.to_stage_id;
    const suggestTargets = async () => {
        try {
            const resp = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/application-stage/suggest-invalidation-targets`,
                { headers, params: { from_stage_id: form.from_stage_id, to_stage_id: form.to_stage_id } }
            );
            const { targets = [], stages_between = [] } = resp?.data?.data || {};
            if (targets.length === 0) {
                toast.info('No stored data to invalidate for the stages between these two.');
                return;
            }
            const config = JSON.stringify({ targets }, null, 2);
            setActions((prev) => {
                const idx = prev.findIndex((a) => a.action_type === 'INVALIDATE_DATA');
                if (idx === -1) return [...prev, { action_type: 'INVALIDATE_DATA', action_config: config }];
                return prev.map((a, i) => (i === idx ? { ...a, action_config: config } : a));
            });
            toast.success(`Suggested ${targets.length} target(s) from ${stages_between.length} stage(s). Review before saving.`);
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to fetch suggested targets');
        }
    };

    const parseActionsForSave = () => {
        return actions.map((a) => {
            let action_config;
            try {
                action_config = JSON.parse(a.action_config || '{}');
            } catch {
                throw new Error(`Invalid JSON in action_config for ${a.action_type}`);
            }
            return { action_type: a.action_type, action_config };
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            let parsedActions;
            try {
                parsedActions = parseActionsForSave();
            } catch (err) {
                toast.error(err.message);
                setSaving(false);
                return;
            }

            const reason_categories = form.reason_categories
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean);

            let stageTransitionId = rule?.stage_transition_id;

            if (isEditing) {
                await axios.put(
                    `${process.env.REACT_APP_API_BASE_URL}/application-stage/update-transition-rule/${stageTransitionId}`,
                    {
                        label: form.label,
                        allowed_role_ids: form.allowed_role_ids.map(Number),
                        requires_two_person_rule: form.requires_two_person_rule,
                        requires_reason: form.requires_reason,
                        reason_categories,
                        is_active: form.is_active,
                    },
                    { headers }
                );
            } else {
                if (form.scope === 'APPLICATION' && (!form.from_stage_id || !form.to_stage_id)) {
                    toast.error('From stage and To stage are required for an APPLICATION-scoped rule');
                    setSaving(false);
                    return;
                }

                const createResp = await axios.post(
                    `${process.env.REACT_APP_API_BASE_URL}/application-stage/create-transition-rule`,
                    {
                        from_stage_id: form.scope === 'APPLICATION' ? Number(form.from_stage_id) : null,
                        to_stage_id: form.scope === 'APPLICATION' ? Number(form.to_stage_id) : null,
                        direction: form.direction,
                        scope: form.scope,
                        allowed_role_ids: form.allowed_role_ids.map(Number),
                        prerequisite_check_codes: [],
                        label: form.label,
                        requires_two_person_rule: form.requires_two_person_rule,
                        requires_reason: form.requires_reason,
                        reason_categories,
                    },
                    { headers }
                );

                stageTransitionId = createResp?.data?.data?.stage_transition_id;
            }

            await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/application-stage/transition-rule/${stageTransitionId}/actions`,
                { actions: parsedActions },
                { headers }
            );

            toast.success(`Rule ${isEditing ? 'updated' : 'created'} successfully`);
            onSaved();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to save rule');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rule-builder-overlay open" onClick={onClose}>
            <div className="rule-builder-drawer open" onClick={(e) => e.stopPropagation()}>
                               <div className="drawer-header">
                    <span className="drawer-title">{isEditing ? 'Edit Transition Rule' : 'Create Transition Rule'}</span>
                    <div className="drawer-header-right">
                        {/* Mobile-only: Cancel + Save next to close icon */}
                        <div className="drawer-header-actions">
                            <Button className="secondary-action" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button className="primary-action" onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving...' : 'Save Rule'}
                            </Button>
                        </div>
                        <Button size="sm" variant="ghost" onClick={onClose} className="close-drawer-btn">
                            <IoMdClose size={18} />
                        </Button>
                    </div>
                </div>

                <div className="drawer-content">
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Label</Form.Label>
                            <Form.Control
                                type="text"
                                value={form.label}
                                onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
                                placeholder="e.g. Loan Sanctioned -> Borrower TVR Pending"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Scope</Form.Label>
                            <Form.Select
                                value={form.scope}
                                disabled={isEditing}
                                onChange={(e) => setForm((p) => ({ ...p, scope: e.target.value }))}
                            >
                                <option value="APPLICATION">APPLICATION</option>
                                <option value="TRANCHE">TRANCHE</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Direction</Form.Label>
                            <Form.Select
                                value={form.direction}
                                disabled={isEditing}
                                onChange={(e) => setForm((p) => ({ ...p, direction: e.target.value }))}
                            >
                                <option value="FORWARD">FORWARD</option>
                                <option value="BACKWARD">BACKWARD</option>
                            </Form.Select>
                        </Form.Group>

                        {form.scope === 'APPLICATION' && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>From Stage</Form.Label>
                                    <Form.Select
                                        value={form.from_stage_id}
                                        disabled={isEditing}
                                        onChange={(e) => setForm((p) => ({ ...p, from_stage_id: e.target.value }))}
                                    >
                                        <option value="">Select stage...</option>
                                        {stages.map((s) => (
                                            <option key={s.stage_id} value={s.stage_id}>
                                                {s.stage_name} ({s.stage_type}){s.hard_lock ? ' — hard-locked' : ''}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>To Stage</Form.Label>
                                    <Form.Select
                                        value={form.to_stage_id}
                                        disabled={isEditing}
                                        onChange={(e) => setForm((p) => ({ ...p, to_stage_id: e.target.value }))}
                                    >
                                        <option value="">Select stage...</option>
                                        {stages.map((s) => (
                                            <option key={s.stage_id} value={s.stage_id}>
                                                {s.stage_name} ({s.stage_type})
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                {hardLockWarning && <div className="rule-builder-warning">{hardLockWarning}</div>}
                            </>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Allowed Roles</Form.Label>
                            <div className="role-checkbox-list">
                                {rolesList?.map((role) => (
                                    <Form.Check
                                        key={role.role_id}
                                        type="checkbox"
                                        label={`${role.role_name} (${role.short_name})`}
                                        checked={form.allowed_role_ids.includes(String(role.role_id))}
                                        onChange={() => toggleRole(role.role_id)}
                                    />
                                ))}
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-3 d-flex align-items-center gap-4">
                            <Form.Check
                                type="checkbox"
                                label="Requires two-person rule"
                                checked={form.requires_two_person_rule}
                                onChange={(e) => setForm((p) => ({ ...p, requires_two_person_rule: e.target.checked }))}
                            />
                            <Form.Check
                                type="checkbox"
                                label="Requires reason"
                                checked={form.requires_reason}
                                onChange={(e) => setForm((p) => ({ ...p, requires_reason: e.target.checked }))}
                            />
                        </Form.Group>

                        {form.requires_reason && (
                            <Form.Group className="mb-3">
                                <Form.Label>Reason Categories (comma-separated)</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={form.reason_categories}
                                    onChange={(e) => setForm((p) => ({ ...p, reason_categories: e.target.value }))}
                                    placeholder="Incorrect TVR data, Customer request, Other"
                                />
                            </Form.Group>
                        )}

                        {isEditing && (
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    label="Active"
                                    checked={form.is_active}
                                    onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                                />
                            </Form.Group>
                        )}

                        <hr />

                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="mb-0">Actions (executed in order)</Form.Label>
                            <div className="d-flex gap-2">
                                {canSuggest && (
                                    <Button size="sm" className="secondary-action" onClick={suggestTargets}>
                                        Suggest INVALIDATE_DATA targets
                                    </Button>
                                )}
                                <Button size="sm" className="secondary-action" onClick={addAction}>
                                    + Add Action
                                </Button>
                            </div>
                        </div>

                        {actions.length === 0 && (
                            <div className="no-actions-hint">No actions yet — add at least one below.</div>
                        )}

                        {actions.map((action, index) => (
                            <div className="action-row" key={index}>
                                <div className="action-row-header">
                                    <span className="action-index">{index + 1}.</span>
                                    <Form.Select
                                        size="sm"
                                        value={action.action_type}
                                        onChange={(e) => updateAction(index, { action_type: e.target.value })}
                                    >
                                        {AUTHORABLE_ACTION_TYPES.map((type) => (
                                            <option key={type} value={type}>
                                                {type}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <div className="action-row-controls">
                                        <Button size="sm" variant="light" onClick={() => moveAction(index, -1)} disabled={index === 0}>
                                            ↑
                                        </Button>
                                        <Button size="sm" variant="light" onClick={() => moveAction(index, 1)} disabled={index === actions.length - 1}>
                                            ↓
                                        </Button>
                                        <Button size="sm" variant="light" onClick={() => removeAction(index)}>
                                            ✕
                                        </Button>
                                    </div>
                                </div>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    className="action-config-textarea"
                                    value={action.action_config}
                                    onChange={(e) => updateAction(index, { action_config: e.target.value })}
                                    placeholder='{"key": "value"}'
                                />
                            </div>
                        ))}

                        <div className="log-transition-note">
                            LOG_TRANSITION is auto-appended as the final action by the backend — no need to add it here.
                        </div>
                    </Form>
                </div>

                <div className="drawer-actions">
                    <Button className="secondary-action" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button className="primary-action" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Rule'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default RuleBuilderDrawer;
